import { Button, Grid, Typography } from "@mui/material";
import React, { useReducer, useState } from "react";
import { GetUserCurrencyTransactionsApiArg } from "src/store/features/api/generated";
import {
  dynamicPriceFormatter,
  dynamicUnitFormatter,
} from "src/utils/helpers/PriceAndUnitsHelpers";
import Preloader from "src/utils/Preloader";
import { initialFilterData } from "../reports/utils/ReportTools";
import TransactionReports from "./TransactionReports";
import {
  createDataMobile,
  createLinesMobile,
  dateStringConversion,
  getDate,
  getTime,
  iconSelector,
  statusSelector,
} from "./helperfns";
import TransactionItem from "./TransactionItem";
import { Data, Daum, FilterData, RawTransactions } from "./types";
import { TransactionsSummaryStyles } from "./styles/TransactionsSummary.styles";

interface TransactionsListProps {
  transactions: RawTransactions;
  isFetching: boolean;
  isSuccess: boolean;
  currentParamState: GetUserCurrencyTransactionsApiArg;
  setParams: (newParams: GetUserCurrencyTransactionsApiArg) => void;
}

export default function TransactionsList(props: TransactionsListProps) {
  const { transactions, isFetching, isSuccess, currentParamState, setParams } = props;

  const classes = TransactionsSummaryStyles();

  initialFilterData.device = "mobile";
  const [reportsFilter, setReportsFilter] = useState<FilterData>(initialFilterData);
  const [state, toggleExportModal] = useReducer((state) => !state, false);

  // load more transactions onClick
  const onSeeMoreTransactions = () => {
    let newLimit: number = 10;
    if (currentParamState.limit) newLimit = currentParamState.limit + 10;
    setParams({ limit: newLimit });
  };

  const generateRandomString = (length: number) => {
    var result = "";
    var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  // render transactions onto UI
  const transactionsList = React.useMemo((): JSX.Element[] => {
    let result: JSX.Element[] = [];
    if (transactions && transactions.data.length > 0) {
      transactions.data.forEach((daum: Daum, idx: number) => {
        const data = daum.data.map((row: Data) => {
          const id = row.id;
          const icon = iconSelector(row.type);
          const type = row.type;
          const date = getDate(row?.completed ?? row?.created ?? 0);
          const time = getTime(row?.completed ?? row?.created ?? 0);
          const currency = row.quoteCurrencyConfig.code;
          const status = statusSelector(row.status);
          const note = row.note;
          const amount = dynamicPriceFormatter(row.value, currency);

          const lines = row.lines.map((line) => {
            const side = line.side ?? "";
            const type = line.currencyConfig.type ?? "";
            const quote = {
              logoUrl: line.quoteCurrencyConfig.logoUrl ?? "",
              code: line.quoteCurrencyConfig.code,
              name: line.quoteCurrencyConfig.displayName,
              type: line.quoteCurrencyConfig.type,
            };

            const base = {
              logoUrl: line.currencyConfig.logoUrl ?? "",
              code: line.currencyConfig.code,
              name: line.currencyConfig.displayName,
              type: line.currencyConfig.type,
            };
            const units = dynamicUnitFormatter(line.units, line.price);
            const price = dynamicPriceFormatter(line.price, currency);
            const value = dynamicPriceFormatter(line.value, currency);
            const fee = dynamicPriceFormatter(line.fee, currency);
            return createLinesMobile(side, type, quote, base, units, price, value, fee);
          });

          return createDataMobile(
            id,
            icon,
            type,
            date,
            time,
            currency,
            amount,
            status,
            lines,
            note,
          );
        });

        result.push(
          <div
            key={generateRandomString(10)}
            style={{ width: "100%" }}
            data-testid="transactions-collapsible-row">
            <Typography className={classes.transactionDate}>{daum.date}</Typography>
            {data.map((item, idx) => (
              <Grid item xs={12} key={item.id} my={1}>
                <TransactionItem date={dateStringConversion(daum.date)} transaction={item} />
              </Grid>
            ))}
          </div>,
        );
      });
    }

    if (!isFetching && transactions && transactions.data.length === 0) {
      result.push(
        <Grid item xs={12} key={"panel-error"}>
          <Typography className={classes.message}>Nothing to see here</Typography>
        </Grid>,
      );
    }
    if (isFetching) {
      result.push(
        <Grid item xs={12} key={"panel-error"}>
          <Preloader />
        </Grid>,
      );
    }
    if (!isFetching && !isSuccess) {
      result.push(
        <Grid item xs={12} key={"panel-error"}>
          <Typography className={classes.noDataMessage}>
            Unable to fetch API data, Please check your connection
          </Typography>
        </Grid>,
      );
    }
    return result;
  }, []);

  return (
    <>
      <Grid container>{transactionsList}</Grid>

      {transactions && transactions.currentPage < transactions.totalPages ? (
        <Grid item xs={12} container justifyContent="center">
          <Grid item className={classes.transactionButtonContainer}>
            <Button
              disableRipple
              fullWidth
              color="secondary"
              variant="contained"
              className={classes.transactionButton}
              onClick={onSeeMoreTransactions}>
              <Typography className={classes.transactionButtonText}>
                See more transactions
              </Typography>
            </Button>
          </Grid>
        </Grid>
      ) : null}
      <TransactionReports
        state={state}
        toggleExportModal={toggleExportModal}
        reportsFilter={reportsFilter}
        setReportsFilter={setReportsFilter}
      />
    </>
  );
}
